import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { AddFieldMappingDropdown } from "./AddFieldMappingDropdown";
import { FieldsList } from "./FieldsList";
import styles from "./ImportConfigurationFieldMappingPage.module.scss";

import {
  getDestinationMetadata,
  getPreview,
  // getSourceEntities,
  getSourceProperties,
  saveImportConfiguration
} from "../../api/template/template";
import { ReactComponent as ArrowBack } from "../../assets/icons/arrowBack-icon.svg";
import { useUserData } from "../../hooks/useUserProfile";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
  selectSliceImportConfigurations,
  selectSliceSelectedImportConfiguration,
  setSliceImportConfigurations,
  setSliceSelectedImportConfiguration
} from "../../store/slices/importConfigurationSlice";
import { selectParseResult } from "../../store/slices/parseResultSlice";
import { setSlicePreview } from "../../store/slices/previewSlice";
import {
  setTemplateSPage,
  setTemplateStep,
} from "../../store/slices/stepSlice";
import { setDemo } from "../../store/slices/uploadFileSlice";
import { PropertyMetadata } from "../../types/entityMetadata";
import { ImportConfiguration, ImportConfigurationFieldMapping } from "../../types/importConfiguration";
import { ETemplatePage, ETemplateStep } from "../../types/steps";
import { createErrorMessage } from "../../utils/createErrorMessage";
import { CustomButton } from "../FormFields/CustomButton";
import { SelectOption } from '../FormFields/CustomTable';
import { Title } from "../Title";

export const ImportConfigurationFieldMappingPage = () => {
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const { organizationId, tenantId } = useUserData();
  const [sourceProperties, setSourceProperties] = useState<SelectOption[]>([]);

  const { data: destinationMetadataData } = useQuery({
      queryKey: ["getDestinationMetadata"],
      queryFn: async () => {
        try {
          const res = await getDestinationMetadata();
          return res.data.data;
        } catch (error) {
          toast.error(createErrorMessage(error));
        }
      },
      enabled: !!tenantId
    });

  const mapToSelectOptions = (metadata: PropertyMetadata[]): SelectOption[] => {
    const temp = metadata.map(item => ({
      label: item.displayName,
      value: item.identifier,
    } as SelectOption));
    // temp.push({ label: null, value: null } as SelectOption);
    return temp;
  };

  const mapImportConfigurationArguments = (importConfiguration: ImportConfiguration): Record<string, string> => {
    const importConfigurationArguments = importConfiguration.importConfigurationArguments
      .reduce<Record<string, string>>((dict, item) => {
        dict[item.identifier] = item.value;
        return dict;
      }, {});
    importConfigurationArguments["FileId"] = parseResult.fileId;
    return importConfigurationArguments;
  };

  const dispatch = useAppDispatch();

  const initialImportConfigurations = useAppSelector(selectSliceImportConfigurations);
  
  const initialSelectedImportConfiguration = useAppSelector(selectSliceSelectedImportConfiguration);
  if (!initialSelectedImportConfiguration) {
    throw new Error('initialSelectedImportConfiguration is null');
  }

  const tempMappings = initialSelectedImportConfiguration.importConfigurationFieldMappings
    .map(item => ({ ...item }));

  const [importConfigurationFieldMappings, setImportConfigurationFieldMappings] = useState<ImportConfigurationFieldMapping[]>(tempMappings);

  const parseResult = useAppSelector(selectParseResult);

  const importConfigurationArguments = mapImportConfigurationArguments(initialSelectedImportConfiguration);

  const { data: sourcePropertiesData } = useQuery({
    queryKey: ["getSourceProperties", tenantId,
      organizationId,
      initialSelectedImportConfiguration?.adapterIdentifier,
      importConfigurationArguments],
    queryFn: async () => {
      try {
        if (!initialSelectedImportConfiguration.sourceIdentifier) {
          throw new Error("sourceIdentifier is null!");
        }
        const res = await getSourceProperties(
          organizationId,
          tenantId,
          initialSelectedImportConfiguration.adapterIdentifier,
          initialSelectedImportConfiguration.sourceIdentifier,
          importConfigurationArguments,
          parseResult.fileId
        );
        return res.data.data;
      } catch (error) {
        toast.error(createErrorMessage(error));
      }
    },
    enabled: !!tenantId
  });

  useEffect(() => {
    validateMappings();

    if (Array.isArray(sourcePropertiesData)) {
      setSourceProperties(mapToSelectOptions(sourcePropertiesData));
    }
  }, [importConfigurationFieldMappings]);

  useEffect(() => {
    if (sourcePropertiesData && sourcePropertiesData.length > 0) {

      const availableSourceIdentifiers = sourcePropertiesData as PropertyMetadata[];

      const sourceToDestinationMatched = importConfigurationFieldMappings.map((mapping) => {
        const isAvailable = availableSourceIdentifiers.some(
          (available) => available.identifier === mapping.sourceFieldIdentifier
        );

        return {
          ...mapping,
          sourceFieldIdentifier: isAvailable ? mapping.sourceFieldIdentifier : null,
        } 
      });

      setImportConfigurationFieldMappings(sourceToDestinationMatched);
    }
  }, [sourcePropertiesData]);

  const nextIsDisabled = useMemo(() => {
    const valid = validationErrors.length === 0;
      return !valid;
  }, [validationErrors]);

  const validateMappings = () => {
    
    const isUnset = (value: any) => value === null || value === undefined || value === "";
    const warnings = importConfigurationFieldMappings.filter(item => (!item?.externallyManaged && isUnset(item.sourceFieldIdentifier)));
    const errors = importConfigurationFieldMappings.filter(item => item?.externallyManaged && isUnset(item.sourceFieldIdentifier));
    
    setValidationErrors(errors);
    setValidationWarnings(warnings);

    errors.forEach(({ destinationFieldIdentifier }) => {
      toast.error(`Source field is not set for ${destinationFieldIdentifier}`);
    });

    warnings.forEach(({ destinationFieldIdentifier }) => {
      toast.warning(`Source field is not set for ${destinationFieldIdentifier}`);
    });

    return { warnings, errors };
  };

  const onBackClick = () => {
    dispatch(setTemplateSPage(ETemplatePage.ImportConfigurationPage));
    dispatch(setTemplateStep(ETemplateStep.step3));
    dispatch(setDemo(null));
  };

  const onNextClick = async () => {
    setSaving(true);
    await onSaveConfiguration();
    
    const importConfigurationArguments = mapImportConfigurationArguments(initialSelectedImportConfiguration);

    const destinationSourceFieldMappings = initialSelectedImportConfiguration.importConfigurationFieldMappings
      .reduce<Record<string, string>>((dict, item) => {
        if (!item.sourceFieldIdentifier) {
          throw new Error('sourceFieldIdentifier is null');
        }
        dict[item.destinationFieldIdentifier] = item.sourceFieldIdentifier;
        return dict;
      }, {});

    if (!initialSelectedImportConfiguration.sourceIdentifier) {
      throw new Error("sourceIdentifier is null!");
    }
    const res = await getPreview(
      organizationId,
      tenantId,
      initialSelectedImportConfiguration.adapterIdentifier,
      initialSelectedImportConfiguration.sourceIdentifier,
      initialSelectedImportConfiguration.destinationIdentifier,
      parseResult.fileId,
      importConfigurationArguments,
      destinationSourceFieldMappings,
    ).catch((error) => {
      toast.error("Ooops! Something went wrong while fetching the preview. Please try again.");
    });

    setSaving(false);

    if (res?.data?.success !== true) {
      return;
    }

    dispatch(setSlicePreview(res.data.data))

    dispatch(setTemplateSPage(ETemplatePage.PreviewPage));
    dispatch(setTemplateStep(ETemplateStep.step5));
  };

  const onSaveConfiguration = async () => {
    try {
      const config = { ...initialSelectedImportConfiguration };
      config.importConfigurationFieldMappings = importConfigurationFieldMappings;
      const res = await saveImportConfiguration(organizationId, config);

      dispatch(setSliceSelectedImportConfiguration(res.data.data));
      const tempMappings = res.data.data.importConfigurationFieldMappings
        .map((item: ImportConfigurationFieldMapping) => {
          return { ...item };
        });
      setImportConfigurationFieldMappings(tempMappings);

      if (!initialImportConfigurations) {
        throw new Error('initialImportConfigurations is null');
      }
      const updatedImportConfigurations = initialImportConfigurations.map(obj =>
        obj.id === config.id ? res.data.data : obj
      );
      dispatch(setSliceImportConfigurations(updatedImportConfigurations));
    } catch (error: any) {
      toast.error("Ooops! Something went wrong while saving the configuration. Please try again.");
    }
  };

  const handleFieldMappingChange = (newFieldMapping: ImportConfigurationFieldMapping, value: string) => {
    const updatedFieldMappings = importConfigurationFieldMappings.map((item) => {
      if (item.id === newFieldMapping.id) {
        return { ...item, sourceFieldIdentifier: value };
      }
      return item;
    });
    setImportConfigurationFieldMappings(updatedFieldMappings);
  };

  const handleFieldMappingDelete = (deletedFieldMapping: ImportConfigurationFieldMapping) => {
    const updatedFieldMappings = importConfigurationFieldMappings.filter((item) => item.id !== deletedFieldMapping.id);
    setImportConfigurationFieldMappings(updatedFieldMappings);
  };

  const handleAddFieldMapping = (destinationFieldIdentifier: string) => {
    const newFieldMapping = {
      destinationFieldIdentifier,
      externallyManaged: false,
      id: -1225,
      importConfigurationId: initialSelectedImportConfiguration.id,
      sourceFieldIdentifier: "",
    }
    const updatedFieldMappings = [...importConfigurationFieldMappings, newFieldMapping];
    setImportConfigurationFieldMappings(updatedFieldMappings);
  }

  return (
    <div className={styles.root}>
      <Title tagName="h1">Field mappings</Title>
      {/* <Alert
        message={
          <Description theme="t2">
            <Space>
              Import sheet {initialSelectedImportConfiguration?.sourceIdentifier}
              from file {parseResult.fileId.replace(/^[^_]*_/, "")}
              to {initialSelectedImportConfiguration?.destinationIdentifier}
            </Space>
          </Description>
        }
        type="info"
      /> */}
        <FieldsList
          importConfiguration={initialSelectedImportConfiguration}
          importConfigurationFieldMappings={importConfigurationFieldMappings}
          sourceProperties={sourceProperties}
          handleFieldMappingChange={handleFieldMappingChange}
          handleFieldMappingDelete={handleFieldMappingDelete}
          sourceFieldsTitle={parseResult.fileId.replace(/^[^_]*_/, "")}
          validationErrors={validationErrors}
          validationWarnings={validationWarnings}
        />
        <AddFieldMappingDropdown
          importConfiguration={initialSelectedImportConfiguration}
          destinationMetadataData={destinationMetadataData}
          handleAddFieldMapping={handleAddFieldMapping}
        />

      <div className={styles.wrapperBtn}>
        <CustomButton
          theme={"secondary"}
          className={styles.btn}
          onClick={onBackClick}
        >
          <ArrowBack className={styles.icon} />
          Back
        </CustomButton>
        <CustomButton
          isLoading={saving}
          isDisabled={nextIsDisabled}
          // className={styles.btn}
          onClick={onNextClick}
        >
          Save and go to preview
        </CustomButton>
      </div>
    </div>
  );
};
